import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { matchBill, payBill, postBill } from "../../api/purchases";

// المطابقة الثلاثية ثم الترحيل ثم السداد — لا ترحيل لفاتورة غير مطابقة.
export const BillWorkflow = ({ bill, onDone }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(bill.totals?.payable || "");
  const [busy, setBusy] = useState(false);
  const problems = bill.match?.problems || [];

  const run = async (step) => {
    setBusy(true);
    try {
      if (step === "match") {
        const res = await matchBill(bill.id);
        toast[res.match?.matched ? "success" : "error"](
          res.match?.matched ? t("purchases.matched") : t("purchases.matchFailed"));
      } else if (step === "post") {
        await postBill(bill.id);
        toast.success(t("purchases.posted"));
      } else {
        await payBill(bill.id, { amount, full: true });
        toast.success(t("purchases.paid"));
      }
      onDone?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.code || t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard className="grid gap-3 p-4" data-testid="bill-workflow">
      <p className="label-xs flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" /> {t("purchases.threeWayMatch")}
      </p>
      {problems.length ? (
        <ul className="grid gap-1" data-testid="bill-match-problems">
          {problems.map((problem, index) => (
            <li key={index} className="text-xs font-bold text-destructive">
              {t("purchases.line")} {problem.line_no}: {t(`purchases.matchCodes.${problem.code}`, problem.code)}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {["draft", "disputed"].includes(bill.state) ? (
          <NeuButton variant="primary" className="px-3 py-2 text-xs" data-testid="bill-match"
                     disabled={busy} onClick={() => run("match")}>
            {t("purchases.runMatch")}
          </NeuButton>
        ) : null}
        {bill.state === "matched" ? (
          <NeuButton variant="primary" className="px-3 py-2 text-xs" data-testid="bill-post"
                     disabled={busy} onClick={() => run("post")}>
            {t("purchases.postToLedger")}
          </NeuButton>
        ) : null}
        {["posted", "partially_paid"].includes(bill.state) ? (
          <>
            <NeuInput className="max-w-[9rem]" data-testid="bill-amount" dir="ltr" value={amount}
                      onChange={(e) => setAmount(e.target.value)} />
            <NeuButton variant="primary" className="px-3 py-2 text-xs" data-testid="bill-pay"
                       disabled={busy} onClick={() => run("pay")}>
              {t("purchases.registerPayment")}
            </NeuButton>
          </>
        ) : null}
        {bill.journal_entry_number ? (
          <span className="num font-mono text-[11px] text-primary" data-testid="bill-journal">
            {t("purchases.journalEntry")}: {bill.journal_entry_number}
          </span>
        ) : null}
      </div>
    </NeuCard>
  );
};
